import Phaser from 'phaser';
import { HEADER_HEIGHT } from '../config';
import { COLORS, TextStyles, Radii } from '../ui/theme';
import { events } from '../state/events';
import type { SceneName } from '../types';

// Shared scaffold for the four "tab" scenes (Menu/Kitchen/Oven/Counter). Each
// of them wants the header bar overlay, a solid theme background, and the
// nav-request → scene.start() wiring.
export abstract class BaseGameScene extends Phaser.Scene {
  protected abstract sceneName(): Extract<SceneName, 'Menu' | 'Kitchen' | 'Oven' | 'Counter'>;
  protected abstract title(): string;
  protected abstract backgroundColor(): number;

  private unsubNav?: () => void;
  private unsubClose?: () => void;

  protected setupChrome(): void {
    const w = this.scale.width;

    this.cameras.main.setBackgroundColor(this.backgroundColor());

    if (!this.scene.isActive('HeaderBar')) this.scene.launch('HeaderBar');
    this.scene.bringToTop('HeaderBar');

    const header = this.scene.get('HeaderBar') as unknown as { setActiveScene(name: SceneName): void };
    header.setActiveScene(this.sceneName());

    const title = this.title();
    if (title) {
      this.add.text(w / 2, HEADER_HEIGHT + 50, title, TextStyles.H1).setOrigin(0.5);
    }

    this.unsubNav = events.on('nav:request', ({ to }) => {
      if (to === this.sceneName()) return;
      this.scene.start(to);
    });
    // Whatever scene the player is in when the closing bell rings, the day
    // is over — head to the summary.
    this.unsubClose = events.on('phase:close', () => {
      if (this.scene.isActive()) this.scene.start('DayEnd');
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubNav?.();
      this.unsubClose?.();
    });
  }

  protected panel(x: number, y: number, w: number, h: number, fill = 0xffffff, alpha = 0.92): Phaser.GameObjects.Graphics {
    const g = this.add.graphics();
    g.fillStyle(fill, alpha);
    g.fillRoundedRect(x, y, w, h, Radii.lg);
    g.lineStyle(2, COLORS.headerPurple, 1);
    g.strokeRoundedRect(x, y, w, h, Radii.lg);
    return g;
  }
}
